import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    FunnelChart,
    Funnel,
    LabelList,
    LineChart,
    Line
} from 'recharts';
import {
    Users,
    FileAudio,
    FolderOpen,
    TrendingUp,
    Clock,
    Mail,
    Activity,
    Database,
    AlertTriangle,
    CheckCircle,
    XCircle,
    RefreshCw,
    Search,
    Filter,
    ChevronDown,
    BarChart3,
    Zap,
    Server,
    Calendar,
    Store,
    Trophy,
    Target,
    Timer,
    Award,
    GitBranch,
    Shield,
    ShieldAlert,
    ShieldX,
    UserX,
    ArrowDownRight,
    Percent,
    Bell,
    MailOpen,
    MailCheck,
    Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';

// KPI Card Component
function KPICard({ title, value, subtitle, icon: Icon, trend, color = "default" }) {
    const colorClasses = {
        default: "border-[#333] bg-[#1A1A1A]",
        green: "border-green-500/30 bg-green-500/5",
        red: "border-red-500/30 bg-red-500/5",
        blue: "border-blue-500/30 bg-blue-500/5",
        amber: "border-amber-500/30 bg-amber-500/5",
        purple: "border-purple-500/30 bg-purple-500/5"
    };
    
    const iconColors = {
        default: "text-[#F51E1E]",
        green: "text-green-400",
        red: "text-red-400",
        blue: "text-blue-400",
        amber: "text-amber-400",
        purple: "text-purple-400"
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border rounded-xl p-5 ${colorClasses[color]}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs text-[#666] uppercase tracking-wider mb-1">{title}</p>
                    <p className="text-3xl font-bold text-white">{value}</p>
                    {subtitle && <p className="text-sm text-[#A3A3A3] mt-1">{subtitle}</p>}
                    {trend !== undefined && (
                        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                            <span>{Math.abs(trend)}% vs previous</span>
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-lg bg-[#262626]`}>
                    <Icon className={`w-6 h-6 ${iconColors[color]}`} />
                </div>
            </div>
        </motion.div>
    );
}

// System Health Card
function SystemHealthCard({ title, status, details, lastUpdate }) {
    const statusColors = {
        healthy: { bg: "bg-green-500/20", text: "text-green-400", icon: CheckCircle },
        warning: { bg: "bg-amber-500/20", text: "text-amber-400", icon: AlertTriangle },
        error: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
        running: { bg: "bg-blue-500/20", text: "text-blue-400", icon: RefreshCw }
    };
    
    const { bg, text, icon: StatusIcon } = statusColors[status] || statusColors.healthy;

    return (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold">{title}</h3>
                <Badge className={`${bg} ${text} border-0`}>
                    <StatusIcon className={`w-3 h-3 mr-1 ${status === 'running' ? 'animate-spin' : ''}`} />
                    {status}
                </Badge>
            </div>
            <div className="space-y-2">
                {Object.entries(details).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                        <span className="text-[#666]">{key}</span>
                        <span className="text-[#A3A3A3]">{value}</span>
                    </div>
                ))}
            </div>
            {lastUpdate && (
                <p className="text-xs text-[#555] mt-3">
                    Last updated: {format(new Date(lastUpdate), 'MMM d, h:mm a')}
                </p>
            )}
        </div>
    );
}

// Log Entry Component
function LogEntry({ event }) {
    const severityColors = {
        info: "border-l-blue-500 bg-blue-500/5",
        warning: "border-l-amber-500 bg-amber-500/5",
        error: "border-l-red-500 bg-red-500/5"
    };
    
    const categoryIcons = {
        user: Users,
        submission: FileAudio,
        project: FolderOpen,
        system: Server,
        security: AlertTriangle
    };
    
    const Icon = categoryIcons[event.category] || Activity;

    return (
        <div className={`border-l-4 ${severityColors[event.severity]} p-3 rounded-r-lg mb-2`}>
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-[#666] mt-0.5" />
                    <div>
                        <p className="text-sm text-white font-medium">{event.event_type}</p>
                        <p className="text-xs text-[#666]">
                            {format(new Date(event.timestamp), 'MMM d, h:mm:ss a')}
                        </p>
                    </div>
                </div>
                <Badge className="bg-[#262626] text-[#A3A3A3] border-0 text-xs">
                    {event.category}
                </Badge>
            </div>
            {event.details && Object.keys(event.details).length > 0 && (
                <div className="mt-2 pl-7 text-xs text-[#888]">
                    {JSON.stringify(event.details)}
                </div>
            )}
        </div>
    );
}

// Operations Alert Card
function OperationsCard({ title, items, icon: Icon, emptyMessage }) {
    return (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-[#F51E1E]" />
                <h3 className="text-white font-semibold">{title}</h3>
                <Badge className="bg-[#F51E1E]/20 text-[#F51E1E] border-0 ml-auto">
                    {items.length}
                </Badge>
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-[#666] text-center py-4">{emptyMessage}</p>
            ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.map((item, idx) => (
                        <div key={idx} className="bg-[#262626] rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-white font-medium truncate">{item.title || item.project || item.track_title}</span>
                                {item.reasons && (
                                    <div className="flex gap-1">
                                        {item.reasons.map((r, i) => (
                                            <Badge key={i} className="bg-amber-500/20 text-amber-400 border-0 text-xs">
                                                {r.replace('_', ' ')}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="text-xs text-[#666] mt-1">
                                {item.poster_name && <span>by {item.poster_name}</span>}
                                {item.submitter && <span>by {item.submitter}</span>}
                                {item.email && <span>{item.email}</span>}
                                {item.age_hours && <span> • {item.age_hours}h old</span>}
                                {item.deadline && <span> • Due {item.deadline}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminAnalyticsDashboardPage() {
    const { token } = useAuth();
    const [timeRange, setTimeRange] = useState('7d');
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // Data states
    const [overview, setOverview] = useState(null);
    const [charts, setCharts] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [operations, setOperations] = useState(null);
    const [logs, setLogs] = useState({ events: [], total: 0 });
    const [marketplace, setMarketplace] = useState(null);
    const [growth, setGrowth] = useState(null);
    const [security, setSecurity] = useState(null);
    const [notificationsData, setNotificationsData] = useState(null);
    
    // Log filters
    const [logCategory, setLogCategory] = useState('all');
    const [logSeverity, setLogSeverity] = useState('all');
    const [logSearch, setLogSearch] = useState('');
    const [debouncedLogSearch, setDebouncedLogSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    
    // Security threshold settings
    const [securityThreshold, setSecurityThreshold] = useState(3);

    // Debounce search input
    const handleSearchChange = (value) => {
        setLogSearch(value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedLogSearch(value);
        }, 500);
    };

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const fetchOverview = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/overview?time_range=${timeRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOverview(data);
            }
        } catch (error) {
            console.error('Error fetching overview:', error);
        }
    }, [API, token, timeRange]);

    const fetchCharts = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/charts?time_range=${timeRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCharts(data);
            }
        } catch (error) {
            console.error('Error fetching charts:', error);
        }
    }, [API, token, timeRange]);

    const fetchSystemHealth = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/system-health`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSystemHealth(data);
            }
        } catch (error) {
            console.error('Error fetching system health:', error);
        }
    }, [API, token]);

    const fetchOperations = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/operations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOperations(data);
            }
        } catch (error) {
            console.error('Error fetching operations:', error);
        }
    }, [API, token]);

    const fetchLogs = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (logCategory && logCategory !== 'all') params.append('category', logCategory);
            if (logSeverity && logSeverity !== 'all') params.append('severity', logSeverity);
            if (debouncedLogSearch) params.append('search', debouncedLogSearch);
            params.append('limit', '50');
            
            const response = await fetch(`${API}/admin/analytics/logs?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setLogs(data);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
        }
    }, [API, token, logCategory, logSeverity, debouncedLogSearch]);

    const fetchMarketplace = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/marketplace?time_range=${timeRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setMarketplace(data);
            }
        } catch (error) {
            console.error('Error fetching marketplace:', error);
        }
    }, [API, token, timeRange]);

    const fetchGrowth = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/growth?time_range=${timeRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setGrowth(data);
            }
        } catch (error) {
            console.error('Error fetching growth:', error);
        }
    }, [API, token, timeRange]);

    const fetchSecurity = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/security?threshold=${securityThreshold}&hours=1`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSecurity(data);
            }
        } catch (error) {
            console.error('Error fetching security:', error);
        }
    }, [API, token, securityThreshold]);

    const fetchNotificationsData = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/analytics/notifications?time_range=${timeRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotificationsData(data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    }, [API, token, timeRange]);

    const fetchAllData = useCallback(async () => {
        setLoading(true);
        await Promise.all([
            fetchOverview(),
            fetchCharts(),
            fetchSystemHealth(),
            fetchOperations(),
            fetchLogs(),
            fetchMarketplace(),
            fetchGrowth(),
            fetchSecurity(),
            fetchNotificationsData()
        ]);
        setLoading(false);
    }, [fetchOverview, fetchCharts, fetchSystemHealth, fetchOperations, fetchLogs, fetchMarketplace, fetchGrowth, fetchSecurity, fetchNotificationsData]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAllData();
        setRefreshing(false);
        toast.success('Dashboard refreshed');
    };

    // Initial data load only - no auto-refresh
    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch time-sensitive data when time range changes
    useEffect(() => {
        if (!loading) {
            fetchOverview();
            fetchCharts();
            fetchMarketplace();
            fetchGrowth();
            fetchNotificationsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    // Refetch logs when filters change
    useEffect(() => {
        if (!loading) {
            fetchLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logCategory, logSeverity, debouncedLogSearch]);

    // Refetch security when threshold changes
    useEffect(() => {
        if (!loading) {
            fetchSecurity();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [securityThreshold]);

    const formatChartDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return format(date, 'MMM d');
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <RefreshCw className="w-8 h-8 text-[#F51E1E] animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <BarChart3 className="w-8 h-8 text-[#F51E1E]" />
                            Analytics Dashboard
                        </h1>
                        <p className="text-[#A3A3A3] mt-1">
                            Real-time insights and system monitoring
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select value={timeRange} onValueChange={setTimeRange}>
                            <SelectTrigger className="w-32 bg-[#262626] border-[#404040] text-white">
                                <Calendar className="w-4 h-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#262626] border-[#404040]">
                                <SelectItem value="24h">Last 24h</SelectItem>
                                <SelectItem value="7d">Last 7 days</SelectItem>
                                <SelectItem value="30d">Last 30 days</SelectItem>
                                <SelectItem value="90d">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={handleRefresh}
                            variant="outline"
                            className="border-[#404040] text-white hover:bg-[#262626]"
                            disabled={refreshing}
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-[#1A1A1A] border border-[#333] p-1 flex-wrap">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="marketplace" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Store className="w-4 h-4 mr-2" />
                            Marketplace
                        </TabsTrigger>
                        <TabsTrigger value="growth" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <GitBranch className="w-4 h-4 mr-2" />
                            Growth
                        </TabsTrigger>
                        <TabsTrigger value="security" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Shield className="w-4 h-4 mr-2" />
                            Security
                        </TabsTrigger>
                        <TabsTrigger value="system" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Server className="w-4 h-4 mr-2" />
                            System Health
                        </TabsTrigger>
                        <TabsTrigger value="operations" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Zap className="w-4 h-4 mr-2" />
                            Operations
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Bell className="w-4 h-4 mr-2" />
                            Notifications
                        </TabsTrigger>
                        <TabsTrigger value="logs" className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white">
                            <Activity className="w-4 h-4 mr-2" />
                            Logs
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6">
                        {/* KPI Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPICard
                                title="New Posters"
                                value={overview?.kpis?.new_posters || 0}
                                subtitle={`of ${overview?.totals?.total_users || 0} total users`}
                                icon={Users}
                                color="purple"
                            />
                            <KPICard
                                title="New Submitters"
                                value={overview?.kpis?.new_submitters || 0}
                                icon={Users}
                                color="blue"
                            />
                            <KPICard
                                title="Projects Posted"
                                value={overview?.kpis?.projects_posted || 0}
                                subtitle={`${overview?.kpis?.active_projects || 0} active`}
                                icon={FolderOpen}
                                color="green"
                            />
                            <KPICard
                                title="Submissions"
                                value={overview?.kpis?.submissions_created || 0}
                                subtitle={`of ${overview?.totals?.total_submissions || 0} total`}
                                icon={FileAudio}
                                color="amber"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <KPICard
                                title="Acceptance Rate"
                                value={`${overview?.kpis?.acceptance_rate || 0}%`}
                                icon={CheckCircle}
                                color="green"
                            />
                            <KPICard
                                title="Pending Invites"
                                value={overview?.kpis?.pending_invites || 0}
                                icon={Mail}
                                color="amber"
                            />
                            <KPICard
                                title="Invite Conversion"
                                value={`${overview?.kpis?.invite_conversion || 0}%`}
                                icon={TrendingUp}
                                color="blue"
                            />
                        </div>

                        {/* Charts */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Submissions Chart */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4">Submissions Over Time</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <AreaChart data={charts?.submissions || []}>
                                        <defs>
                                            <linearGradient id="colorOrganic" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#F51E1E" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#F51E1E" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="colorInvited" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={formatChartDate}
                                            stroke="#666"
                                            fontSize={12}
                                        />
                                        <YAxis stroke="#666" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#262626', 
                                                border: '1px solid #404040',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Legend />
                                        <Area 
                                            type="monotone" 
                                            dataKey="organic" 
                                            name="Organic"
                                            stroke="#F51E1E" 
                                            fillOpacity={1}
                                            fill="url(#colorOrganic)"
                                            stackId="1"
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="invited" 
                                            name="Invited"
                                            stroke="#8B5CF6" 
                                            fillOpacity={1}
                                            fill="url(#colorInvited)"
                                            stackId="1"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Signups Chart */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4">Signups Over Time</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={charts?.signups || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={formatChartDate}
                                            stroke="#666"
                                            fontSize={12}
                                        />
                                        <YAxis stroke="#666" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#262626', 
                                                border: '1px solid #404040',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Legend />
                                        <Bar dataKey="posters" name="Posters" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="submitters" name="Submitters" fill="#22C55E" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Projects Chart */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4">Projects Posted Over Time</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={charts?.projects || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={formatChartDate}
                                        stroke="#666"
                                        fontSize={12}
                                    />
                                    <YAxis stroke="#666" fontSize={12} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#262626', 
                                            border: '1px solid #404040',
                                            borderRadius: '8px'
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="count" name="Projects" fill="#F51E1E" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </TabsContent>

                    {/* Marketplace Health Tab */}
                    <TabsContent value="marketplace" className="space-y-6">
                        {/* Overall Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPICard
                                title="Overall Acceptance Rate"
                                value={`${marketplace?.overall?.acceptance_rate || 0}%`}
                                subtitle={`${marketplace?.overall?.accepted || 0} of ${(marketplace?.overall?.accepted || 0) + (marketplace?.overall?.rejected || 0)} reviewed`}
                                icon={Target}
                                color="green"
                            />
                            <KPICard
                                title="Avg Response Time"
                                value={marketplace?.overall?.avg_response_hours ? `${marketplace.overall.avg_response_hours}h` : 'N/A'}
                                subtitle="Time to decision"
                                icon={Timer}
                                color="blue"
                            />
                            <KPICard
                                title="Pending Review"
                                value={marketplace?.overall?.pending || 0}
                                subtitle="Awaiting decision"
                                icon={Clock}
                                color="amber"
                            />
                            <KPICard
                                title="Total Submissions"
                                value={marketplace?.overall?.total_submissions || 0}
                                icon={FileAudio}
                                color="purple"
                            />
                        </div>

                        {/* Acceptance Rate by Category Chart */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <Target className="w-5 h-5 text-[#F51E1E]" />
                                Acceptance Rate by Category
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={marketplace?.by_category || []} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis type="number" domain={[0, 100]} stroke="#666" fontSize={12} />
                                    <YAxis 
                                        type="category" 
                                        dataKey="category" 
                                        stroke="#666" 
                                        fontSize={12} 
                                        width={100}
                                        tickFormatter={(val) => val.length > 12 ? val.substring(0, 12) + '...' : val}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#262626', 
                                            border: '1px solid #404040',
                                            borderRadius: '8px'
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                        formatter={(value, name) => [
                                            name === 'acceptance_rate' ? `${value}%` : value,
                                            name === 'acceptance_rate' ? 'Acceptance Rate' : name
                                        ]}
                                    />
                                    <Bar 
                                        dataKey="acceptance_rate" 
                                        fill="#22C55E" 
                                        radius={[0, 4, 4, 0]}
                                        name="Acceptance Rate"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Top Posters Leaderboard */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Trophy className="w-5 h-5 text-amber-400" />
                                    <h3 className="text-white font-semibold">Top Posters (by Submissions)</h3>
                                </div>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                    {(marketplace?.by_poster || []).map((poster, idx) => (
                                        <div 
                                            key={poster.id} 
                                            className="bg-[#262626] rounded-lg p-3 flex items-center gap-3"
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                                idx === 0 ? 'bg-amber-500 text-black' :
                                                idx === 1 ? 'bg-gray-400 text-black' :
                                                idx === 2 ? 'bg-amber-700 text-white' :
                                                'bg-[#333] text-[#A3A3A3]'
                                            }`}>
                                                {idx + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium truncate">{poster.name}</p>
                                                {poster.company && (
                                                    <p className="text-xs text-[#666] truncate">{poster.company}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-white font-bold">{poster.total_submissions}</p>
                                                <p className="text-xs text-[#666]">submissions</p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-bold ${
                                                    poster.acceptance_rate >= 50 ? 'text-green-400' :
                                                    poster.acceptance_rate >= 25 ? 'text-amber-400' :
                                                    'text-red-400'
                                                }`}>
                                                    {poster.acceptance_rate}%
                                                </p>
                                                <p className="text-xs text-[#666]">accept rate</p>
                                            </div>
                                            {poster.avg_response_hours && (
                                                <div className="text-right">
                                                    <p className="text-blue-400 font-bold">{poster.avg_response_hours}h</p>
                                                    <p className="text-xs text-[#666]">avg resp.</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {(!marketplace?.by_poster || marketplace.by_poster.length === 0) && (
                                        <p className="text-center text-[#666] py-4">No poster data available</p>
                                    )}
                                </div>
                            </div>

                            {/* Top Submitters Leaderboard */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Award className="w-5 h-5 text-purple-400" />
                                    <h3 className="text-white font-semibold">Top Artists (by Acceptances)</h3>
                                </div>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                    {(marketplace?.top_submitters || []).map((submitter, idx) => (
                                        <div 
                                            key={submitter.id} 
                                            className="bg-[#262626] rounded-lg p-3 flex items-center gap-3"
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                                idx === 0 ? 'bg-purple-500 text-white' :
                                                idx === 1 ? 'bg-purple-400 text-white' :
                                                idx === 2 ? 'bg-purple-300 text-black' :
                                                'bg-[#333] text-[#A3A3A3]'
                                            }`}>
                                                {idx + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium truncate">{submitter.name}</p>
                                                <p className="text-xs text-[#666]">{submitter.total_submissions} total submissions</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-green-400 font-bold">{submitter.accepted}</p>
                                                <p className="text-xs text-[#666]">accepted</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-white font-bold">{submitter.acceptance_rate}%</p>
                                                <p className="text-xs text-[#666]">success rate</p>
                                            </div>
                                        </div>
                                    ))}
                                    {(!marketplace?.top_submitters || marketplace.top_submitters.length === 0) && (
                                        <p className="text-center text-[#666] py-4">No submitter data available</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Detailed Category Breakdown Table */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <FolderOpen className="w-5 h-5 text-[#F51E1E]" />
                                Category Breakdown
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-[#333]">
                                            <th className="text-left text-xs text-[#666] font-medium pb-3 pr-4">Category</th>
                                            <th className="text-right text-xs text-[#666] font-medium pb-3 px-4">Total</th>
                                            <th className="text-right text-xs text-[#666] font-medium pb-3 px-4">Accepted</th>
                                            <th className="text-right text-xs text-[#666] font-medium pb-3 px-4">Rejected</th>
                                            <th className="text-right text-xs text-[#666] font-medium pb-3 pl-4">Accept Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(marketplace?.by_category || []).map((cat) => (
                                            <tr key={cat.category} className="border-b border-[#262626]">
                                                <td className="py-3 pr-4 text-white">{cat.category}</td>
                                                <td className="py-3 px-4 text-right text-[#A3A3A3]">{cat.total}</td>
                                                <td className="py-3 px-4 text-right text-green-400">{cat.accepted}</td>
                                                <td className="py-3 px-4 text-right text-red-400">{cat.rejected}</td>
                                                <td className="py-3 pl-4 text-right">
                                                    <Badge className={`${
                                                        cat.acceptance_rate >= 50 ? 'bg-green-500/20 text-green-400' :
                                                        cat.acceptance_rate >= 25 ? 'bg-amber-500/20 text-amber-400' :
                                                        'bg-red-500/20 text-red-400'
                                                    } border-0`}>
                                                        {cat.acceptance_rate}%
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {(!marketplace?.by_category || marketplace.by_category.length === 0) && (
                                    <p className="text-center text-[#666] py-8">No category data available</p>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Growth & Conversion Tab */}
                    <TabsContent value="growth" className="space-y-6">
                        {/* Conversion Rate KPIs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPICard
                                title="Invite → Accept"
                                value={`${growth?.conversion_rates?.invite_to_accept || 0}%`}
                                subtitle={`${growth?.totals?.invites_accepted || 0} of ${growth?.totals?.invites_sent || 0}`}
                                icon={Mail}
                                color="blue"
                            />
                            <KPICard
                                title="Accept → Submit"
                                value={`${growth?.conversion_rates?.accept_to_submit || 0}%`}
                                subtitle={`${growth?.totals?.first_submissions || 0} submitted`}
                                icon={FileAudio}
                                color="purple"
                            />
                            <KPICard
                                title="Submit → Accepted"
                                value={`${growth?.conversion_rates?.submit_to_accepted || 0}%`}
                                subtitle={`${growth?.totals?.submissions_accepted || 0} accepted`}
                                icon={CheckCircle}
                                color="green"
                            />
                            <KPICard
                                title="Overall Conversion"
                                value={`${growth?.conversion_rates?.overall || 0}%`}
                                subtitle="Invite to accepted"
                                icon={TrendingUp}
                                color="amber"
                            />
                        </div>

                        {/* Funnel Visualization */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <GitBranch className="w-5 h-5 text-[#F51E1E]" />
                                Conversion Funnel
                            </h3>
                            <div className="grid grid-cols-4 gap-4">
                                {(growth?.funnel || []).map((stage, idx) => (
                                    <div key={stage.stage} className="relative">
                                        <div 
                                            className={`rounded-lg p-4 text-center ${
                                                idx === 0 ? 'bg-blue-500/20 border border-blue-500/30' :
                                                idx === 1 ? 'bg-purple-500/20 border border-purple-500/30' :
                                                idx === 2 ? 'bg-amber-500/20 border border-amber-500/30' :
                                                'bg-green-500/20 border border-green-500/30'
                                            }`}
                                        >
                                            <p className="text-3xl font-bold text-white">{stage.count}</p>
                                            <p className="text-sm text-[#A3A3A3] mt-1">{stage.stage}</p>
                                            <p className={`text-xs mt-2 ${
                                                idx === 0 ? 'text-blue-400' :
                                                idx === 1 ? 'text-purple-400' :
                                                idx === 2 ? 'text-amber-400' :
                                                'text-green-400'
                                            }`}>
                                                {stage.rate}% of total
                                            </p>
                                        </div>
                                        {idx < 3 && (
                                            <div className="absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                                                <ArrowDownRight className="w-4 h-4 text-[#666] rotate-[-45deg]" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {(!growth?.funnel || growth.funnel.length === 0) && (
                                <p className="text-center text-[#666] py-8">No funnel data available</p>
                            )}
                        </div>

                        {/* Daily Conversion Trend */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-[#F51E1E]" />
                                Daily Invite Performance
                            </h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={growth?.daily_trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={formatChartDate}
                                        stroke="#666"
                                        fontSize={12}
                                    />
                                    <YAxis stroke="#666" fontSize={12} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#262626', 
                                            border: '1px solid #404040',
                                            borderRadius: '8px'
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Legend />
                                    <Bar dataKey="invites_sent" name="Invites Sent" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="invites_accepted" name="Invites Accepted" fill="#22C55E" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Conversion Rate Trend */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <Percent className="w-5 h-5 text-[#F51E1E]" />
                                Daily Conversion Rate
                            </h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={growth?.daily_trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={formatChartDate}
                                        stroke="#666"
                                        fontSize={12}
                                    />
                                    <YAxis stroke="#666" fontSize={12} domain={[0, 100]} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#262626', 
                                            border: '1px solid #404040',
                                            borderRadius: '8px'
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                        formatter={(value) => [`${value}%`, 'Conversion Rate']}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="conversion_rate" 
                                        name="Conversion Rate"
                                        stroke="#F51E1E" 
                                        strokeWidth={2}
                                        dot={{ fill: '#F51E1E', strokeWidth: 2 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </TabsContent>

                    {/* Security Tab */}
                    <TabsContent value="security" className="space-y-6">
                        {/* Security KPIs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPICard
                                title="Failed Logins (24h)"
                                value={security?.summary?.failed_logins_24h || 0}
                                subtitle={`${security?.summary?.failure_rate_24h || 0}% failure rate`}
                                icon={ShieldX}
                                color={security?.summary?.failed_logins_24h > 10 ? "red" : "amber"}
                            />
                            <KPICard
                                title="Failed Logins (7d)"
                                value={security?.summary?.failed_logins_7d || 0}
                                icon={UserX}
                                color="amber"
                            />
                            <KPICard
                                title="Successful Logins (24h)"
                                value={security?.summary?.successful_logins_24h || 0}
                                icon={CheckCircle}
                                color="green"
                            />
                            <KPICard
                                title="Suspicious Accounts"
                                value={security?.summary?.suspicious_accounts || 0}
                                subtitle={`≥${securityThreshold} failures/hour`}
                                icon={ShieldAlert}
                                color={security?.summary?.suspicious_accounts > 0 ? "red" : "green"}
                            />
                        </div>

                        {/* Threshold Slider */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-white font-semibold flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-[#F51E1E]" />
                                        Suspicious Activity Threshold
                                    </h3>
                                    <p className="text-sm text-[#666] mt-1">
                                        Flag accounts with this many failed logins within 1 hour
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-white">{securityThreshold}</span>
                                    <span className="text-[#666] ml-1">attempts</span>
                                </div>
                            </div>
                            <Slider
                                value={[securityThreshold]}
                                onValueChange={(value) => setSecurityThreshold(value[0])}
                                min={2}
                                max={10}
                                step={1}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-[#666] mt-2">
                                <span>Strict (2)</span>
                                <span>Moderate (5)</span>
                                <span>Lenient (10)</span>
                            </div>
                        </div>

                        {/* Suspicious Activity List */}
                        {security?.suspicious_activity?.length > 0 && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                                <h3 className="text-red-400 font-semibold mb-4 flex items-center gap-2">
                                    <ShieldAlert className="w-5 h-5" />
                                    Suspicious Activity Detected ({security.suspicious_activity.length})
                                </h3>
                                <div className="space-y-3">
                                    {security.suspicious_activity.map((item, idx) => (
                                        <div key={idx} className="bg-[#1A1A1A] rounded-lg p-4 flex items-center justify-between">
                                            <div>
                                                <p className="text-white font-medium">{item.email}</p>
                                                <p className="text-xs text-[#666]">
                                                    {item.failed_attempts} failed attempts • Last: {format(new Date(item.last_attempt), 'MMM d, h:mm a')}
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                {item.reasons.map((reason, i) => (
                                                    <Badge key={i} className="bg-red-500/20 text-red-400 border-0 text-xs">
                                                        {reason.replace('_', ' ')}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Login Trend Chart */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-[#F51E1E]" />
                                    Login Attempts (7 days)
                                </h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={security?.daily_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={formatChartDate}
                                            stroke="#666"
                                            fontSize={12}
                                        />
                                        <YAxis stroke="#666" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#262626', 
                                                border: '1px solid #404040',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Legend />
                                        <Bar dataKey="successful" name="Successful" fill="#22C55E" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="failed" name="Failed" fill="#EF4444" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Recent Failed Logins */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                    <XCircle className="w-5 h-5 text-red-400" />
                                    Recent Failed Logins
                                </h3>
                                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                                    {(security?.recent_failed_logins || []).length === 0 ? (
                                        <p className="text-center text-[#666] py-4">No failed logins recorded</p>
                                    ) : (
                                        security.recent_failed_logins.map((event, idx) => (
                                            <div key={idx} className="bg-[#262626] rounded-lg p-3 flex items-center justify-between">
                                                <div>
                                                    <p className="text-white text-sm font-medium">
                                                        {event.details?.email || 'Unknown'}
                                                    </p>
                                                    <p className="text-xs text-[#666]">
                                                        {format(new Date(event.timestamp), 'MMM d, h:mm:ss a')}
                                                    </p>
                                                </div>
                                                <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">
                                                    {event.details?.reason?.replace('_', ' ') || 'failed'}
                                                </Badge>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* System Health Tab */}
                    <TabsContent value="system" className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Scraper Health */}
                            <SystemHealthCard
                                title="HRDRV Scraper"
                                status={
                                    systemHealth?.scraper?.is_running ? 'running' :
                                    systemHealth?.scraper?.last_run_status === 'success' ? 'healthy' :
                                    systemHealth?.scraper?.last_run_status === 'error' ? 'error' : 'healthy'
                                }
                                details={{
                                    'Status': systemHealth?.scraper?.enabled ? 'Enabled' : 'Disabled',
                                    'Last Run': systemHealth?.scraper?.last_run_at ? 
                                        format(new Date(systemHealth.scraper.last_run_at), 'MMM d, h:mm a') : 'Never',
                                    'Last Status': systemHealth?.scraper?.last_run_status || 'N/A',
                                    'Opportunities': systemHealth?.scraper?.total_opportunities || 0
                                }}
                                lastUpdate={systemHealth?.timestamp}
                            />

                            {/* Database Health */}
                            <SystemHealthCard
                                title="Database"
                                status="healthy"
                                details={{
                                    'Users': systemHealth?.database?.users || 0,
                                    'Submissions': systemHealth?.database?.submissions || 0,
                                    'Projects': systemHealth?.database?.poster_projects || 0,
                                    'Events Logged': systemHealth?.database?.events || 0
                                }}
                                lastUpdate={systemHealth?.timestamp}
                            />

                            {/* Events Summary */}
                            <SystemHealthCard
                                title="Events (24h)"
                                status={
                                    systemHealth?.events_24h?.by_severity?.error > 5 ? 'error' :
                                    systemHealth?.events_24h?.by_severity?.warning > 10 ? 'warning' : 'healthy'
                                }
                                details={{
                                    'Total Events': systemHealth?.events_24h?.total || 0,
                                    'Info': systemHealth?.events_24h?.by_severity?.info || 0,
                                    'Warnings': systemHealth?.events_24h?.by_severity?.warning || 0,
                                    'Errors': systemHealth?.events_24h?.by_severity?.error || 0
                                }}
                                lastUpdate={systemHealth?.timestamp}
                            />
                        </div>

                        {/* Event Categories */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <h3 className="text-white font-semibold mb-4">Events by Category (24h)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {Object.entries(systemHealth?.events_24h?.by_category || {}).map(([category, count]) => (
                                    <div key={category} className="bg-[#262626] rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-white">{count}</p>
                                        <p className="text-sm text-[#A3A3A3] capitalize">{category}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Operations Tab */}
                    <TabsContent value="operations" className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <OperationsCard
                                title="Projects Needing Attention"
                                items={operations?.projects_needing_attention || []}
                                icon={AlertTriangle}
                                emptyMessage="All projects are in good shape!"
                            />
                            <OperationsCard
                                title="Unreviewed Submissions"
                                items={operations?.unreviewed_submissions || []}
                                icon={Clock}
                                emptyMessage="No old unreviewed submissions"
                            />
                            <OperationsCard
                                title="Pending Invites (Expiring Soon)"
                                items={operations?.pending_invites || []}
                                icon={Mail}
                                emptyMessage="No invites expiring soon"
                            />
                        </div>
                    </TabsContent>

                    {/* Notifications Tab */}
                    <TabsContent value="notifications" className="space-y-6">
                        {/* In-App Notification KPIs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPICard
                                title="Notifications Sent"
                                value={notificationsData?.in_app?.total_sent || 0}
                                icon={Bell}
                                color="blue"
                            />
                            <KPICard
                                title="Read"
                                value={notificationsData?.in_app?.read || 0}
                                subtitle={`${notificationsData?.in_app?.read_rate || 0}% read rate`}
                                icon={Eye}
                                color="green"
                            />
                            <KPICard
                                title="Unread"
                                value={notificationsData?.in_app?.unread || 0}
                                icon={MailOpen}
                                color="amber"
                            />
                            <KPICard
                                title="Acceptances Notified"
                                value={notificationsData?.by_type?.submission_accepted || 0}
                                icon={CheckCircle}
                                color="green"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Notification Breakdown by Type */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-[#F51E1E]" />
                                    Notifications by Type
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-3 bg-[#262626] rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                            <span className="text-white">Track Accepted</span>
                                        </div>
                                        <span className="text-2xl font-bold text-green-400">
                                            {notificationsData?.by_type?.submission_accepted || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-[#262626] rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <XCircle className="w-5 h-5 text-red-400" />
                                            <span className="text-white">Track Rejected</span>
                                        </div>
                                        <span className="text-2xl font-bold text-red-400">
                                            {notificationsData?.by_type?.submission_rejected || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-[#262626] rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-blue-400" />
                                            <span className="text-white">Invite Received</span>
                                        </div>
                                        <span className="text-2xl font-bold text-blue-400">
                                            {notificationsData?.by_type?.invite_received || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Daily Notification Trend */}
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-[#F51E1E]" />
                                    Daily Notification Activity
                                </h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={notificationsData?.daily_trend || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={formatChartDate}
                                            stroke="#666"
                                            fontSize={12}
                                        />
                                        <YAxis stroke="#666" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#262626', 
                                                border: '1px solid #404040',
                                                borderRadius: '8px'
                                            }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                        <Legend />
                                        <Bar dataKey="sent" name="Sent" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="read" name="Read" fill="#22C55E" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* GHL Email Integration Status */}
                        <div className={`border rounded-xl p-5 ${notificationsData?.ghl_email?.webhook_configured ? 'bg-green-500/10 border-green-500/30' : 'bg-[#1A1A1A] border-[#333]'}`}>
                            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <MailCheck className="w-5 h-5 text-[#F51E1E]" />
                                GHL Email Tracking
                            </h3>
                            {notificationsData?.ghl_email?.webhook_configured ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="text-center p-3 bg-[#262626] rounded-lg">
                                        <p className="text-2xl font-bold text-white">{notificationsData?.ghl_email?.emails_sent || 0}</p>
                                        <p className="text-xs text-[#666]">Emails Sent</p>
                                    </div>
                                    <div className="text-center p-3 bg-[#262626] rounded-lg">
                                        <p className="text-2xl font-bold text-white">{notificationsData?.ghl_email?.emails_delivered || 0}</p>
                                        <p className="text-xs text-[#666]">Delivered</p>
                                    </div>
                                    <div className="text-center p-3 bg-[#262626] rounded-lg">
                                        <p className="text-2xl font-bold text-green-400">{notificationsData?.ghl_email?.open_rate || 0}%</p>
                                        <p className="text-xs text-[#666]">Open Rate</p>
                                    </div>
                                    <div className="text-center p-3 bg-[#262626] rounded-lg">
                                        <p className="text-2xl font-bold text-blue-400">{notificationsData?.ghl_email?.click_rate || 0}%</p>
                                        <p className="text-xs text-[#666]">Click Rate</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <Mail className="w-12 h-12 text-[#444] mx-auto mb-3" />
                                    <p className="text-[#888] mb-2">GHL email tracking not configured</p>
                                    <p className="text-xs text-[#666]">
                                        Configure GHL webhooks to track email opens, clicks, and delivery rates
                                    </p>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Logs Tab */}
                    <TabsContent value="logs" className="space-y-6">
                        {/* Filters */}
                        <div className="flex items-center gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    placeholder="Search logs..."
                                    value={logSearch}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="pl-10 bg-[#262626] border-[#404040] text-white"
                                />
                            </div>
                            <Select value={logCategory} onValueChange={setLogCategory}>
                                <SelectTrigger className="w-40 bg-[#262626] border-[#404040] text-white">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#262626] border-[#404040]">
                                    <SelectItem value="all">All Categories</SelectItem>
                                    <SelectItem value="user">User</SelectItem>
                                    <SelectItem value="submission">Submission</SelectItem>
                                    <SelectItem value="project">Project</SelectItem>
                                    <SelectItem value="system">System</SelectItem>
                                    <SelectItem value="security">Security</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={logSeverity} onValueChange={setLogSeverity}>
                                <SelectTrigger className="w-40 bg-[#262626] border-[#404040] text-white">
                                    <SelectValue placeholder="Severity" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#262626] border-[#404040]">
                                    <SelectItem value="all">All Severities</SelectItem>
                                    <SelectItem value="info">Info</SelectItem>
                                    <SelectItem value="warning">Warning</SelectItem>
                                    <SelectItem value="error">Error</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Log Entries */}
                        <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-white font-semibold">Event Logs</h3>
                                <Badge className="bg-[#262626] text-[#A3A3A3] border-0">
                                    {logs.total} total events
                                </Badge>
                            </div>
                            <div className="max-h-[600px] overflow-y-auto">
                                {logs.events.length === 0 ? (
                                    <p className="text-center text-[#666] py-8">No events found</p>
                                ) : (
                                    logs.events.map((event) => (
                                        <LogEntry key={event.id} event={event} />
                                    ))
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
